/* eslint-disable curly, indent, no-unused-expressions, no-magic-numbers */
import ResizeTracker from 'utils/ResizeTracker';

/**
 * @module SearchCategories
 */
class SearchCategories {

    /**
     * @param {HTMLElement} element - The HTMLElement this module is constructed upon
     * @param {Object} options - ConditionerJS's merged options
     */
    constructor(element, options) {
        this._element = element;
        this._options = options;

        this._state = {
            maxResults: 0,
            results: 0,
            isMobile: false,
            headerHeight: 0,
            filtersVisible: false,
            mobileSize: 768,
            activeCategories: []
        };

        this._rootContainer = document.body;

        this._elements = {
            categoryItems: Array.from(this._element.querySelectorAll(this._options.categoryItems)),
            categoryItemsLinks: Array.from(this._element.querySelectorAll(this._options.categoryItemsLinks)),
            sidebarList: this._element.querySelector(this._options.sidebarList),
            filterToggle: this._element.querySelector(this._options.filterToggle),
            sections: Array.from(this._element.querySelectorAll(this._options.sections)),
            resetButton: this._element.querySelector(this._options.resetButton),
            header: document.querySelector('.header'),
            liveRegion: document.querySelector(`#${this._options.liveRegion}`),
            liveRegionCounter: document.querySelector(`#${this._options.liveRegion} .counter`),
            liveRegionTag: document.querySelector(`#${this._options.liveRegion} .tag`),
            liveRegionPrefix: document.querySelector(`#${this._options.liveRegion} .prefix`)
        };

        this._initialize();
    }

    /**
     * Initialize
     */
    _initialize() {
        this._resizeTracker = new ResizeTracker();
        this._resizeTracker.init(
            { element: this._rootContainer },
            this._setIsMobile
        );

        this._setIsMobile();

        // Add click event to all sidebar items
        this._elements.categoryItemsLinks.forEach(category => {
            category.addEventListener('click', event => this._clickedCategory(event));
            category.addEventListener('keydown', event => this._handleBtnKeyDown(event));
        });


        // Add click event the reset button
        this._elements.resetButton.addEventListener('click', event => this._resetCategories(event));
        this._elements.filterToggle.addEventListener('click', event => this._toggleFilter(event));
        this._setLiveRegionOptions();
        this._initFilters();
    }

    _handleBtnKeyDown(event) {
        if (event.key === ' ' || event.key === 'Enter' || event.key === 'Spacebar') {
            this._clickedCategory(event);
        }
    }

    /**
     * Set mobile state
     * @private
     */
    _setIsMobile() {
        if (!this._rootContainer) return;
        // eslint-disable-next-line no-magic-numbers
        const em = parseFloat(getComputedStyle(this._elements.header).fontSize) * 9;
        this._state.headerHeight = this._elements.header.offsetHeight + em;
        this._state.isMobile = this._rootContainer.clientWidth < this._state.mobileSize;
        this._state.isMobile && this._elements.filterToggle.setAttribute('aria-expanded', this._state.filtersVisible);
        this._state.isMobile && this._elements.sidebarList.setAttribute('aria-hidden', !this._state.filtersVisible);
        !this._state.isMobile && this._elements.sidebarList.hasAttribute('aria-hidden') && this._elements.sidebarList.removeAttribute('aria-hidden');
    }


    /**
     * When someone clicks on the category
     * @param {*} event the click event
     */
    _clickedCategory(event) {
        event.preventDefault();
        const isDisabled = event.target.getAttribute('disabled');
        if (isDisabled) return;
        const listItem = event.currentTarget.parentNode;
        const isActive = listItem.classList.contains('active');
        event.currentTarget.setAttribute('aria-pressed', !isActive);
        !isActive && listItem.classList.add('active');
        isActive && listItem.classList.remove('active');
        const hasActiveCategory = this._element.querySelector('.search-categories__sidebar-item.active');
        hasActiveCategory ? this._showActiveSection(event.currentTarget) : this._showAllSections();
    }

    /**
     * Toggle filters visibility
     * @param {*} event the click event
     */
    _toggleFilter(event) {
        event.preventDefault();
        this._state.filtersVisible = !this._state.filtersVisible;
        this._elements.filterToggle.setAttribute('aria-expanded', this._state.filtersVisible);
        this._elements.sidebarList.setAttribute('aria-hidden', !this._state.filtersVisible);
    }

    _initFilters() {
        const activeFilter = this._elements.sidebarList.querySelector('.search-categories__sidebar-item.active') instanceof HTMLElement;
        this._state.isMobile && this._elements.filterToggle.setAttribute('aria-expanded', activeFilter);
        this._state.isMobile && this._elements.sidebarList.setAttribute('aria-hidden', !activeFilter);
        this._elements.sections.forEach(section => {
            const questions = section.querySelectorAll('.search-categories__question-inside');
            this._state.results = this._state.results + questions.length;
        });
        this._state.maxResults = this._state.results;
        this._handleLiveRegionChange();
    }

    /**
     * Reset categories filter
     */
    _resetCategories() {
        this._elements.categoryItems.forEach(category => {
            category.classList.remove('active');
        });

        this._elements.categoryItemsLinks.forEach(category => {
            category.setAttribute('aria-pressed', false);
        });

        this._showAllSections();
    }

    /**
     * Show / un-hide the active section
     * @param {element} activeCategory
     */
    _showActiveSection() {
        this._state.results = 0;
        this._elements.sections.forEach((section, index) => {
            const isActive = this._elements.categoryItems[index].classList.contains('active');

            const sectionLink = section.querySelector('[data-trigger]');
            if (!sectionLink) return;

            isActive && this._state.activeCategories.push(sectionLink.textContent);
            const questions = sectionLink.nextElementSibling.querySelectorAll('.search-categories__question-inside');
            this._state.results = isActive ? this._state.results + questions.length : this._state.results;
            sectionLink && sectionLink.setAttribute('aria-expanded', isActive);
            sectionLink && sectionLink.nextElementSibling.setAttribute('aria-hidden', !isActive);
            section.setAttribute('aria-hidden', !isActive);
        });
        this._element.setAttribute('data-filtered', true);
        this._handleLiveRegionChange();
    }

    /**
     * Add the 'hide' class to all section elements
     */
    _hideSections() {
        this._elements.sections.forEach(section => {
            section.setAttribute('aria-hidden', true);
            const sectionLink = section.querySelector('[data-trigger]');
            sectionLink && sectionLink.setAttribute('aria-expanded', false);
            sectionLink && sectionLink.nextElementSibling.setAttribute('aria-hidden', true);
            section.classList.remove('active');
        });
        this._state.activeCategories = [];
        this._state.results = 0;
        this._handleLiveRegionChange();
    }

    /**
     * Add the 'hide' class to all section elements
     */
    _showAllSections() {
        this._state.results = 0;
        this._elements.sections.forEach(section => {
            section.setAttribute('aria-hidden', false);
            const sectionLink = section.querySelector('[data-trigger]');
            if (!sectionLink) return;

            sectionLink.setAttribute('aria-expanded', false);
            sectionLink.nextElementSibling.setAttribute('aria-hidden', true);
            const questions = sectionLink.nextElementSibling.querySelectorAll('.search-categories__question-inside');
            this._state.results = this._state.results + questions.length;
            section.classList.remove('active');
        });
        this._state.activeCategories = [];
        this._element.setAttribute('data-filtered', false);
        this._handleLiveRegionChange();

    }

    _setLiveRegionOptions() {
        const liveRegionLabels = {
            multiple: this._elements.liveRegion.getAttribute('data-multiple'),
            single: this._elements.liveRegion.getAttribute('data-single'),
            empty: this._elements.liveRegion.getAttribute('data-empty'),
            all: this._elements.liveRegion.getAttribute('data-all')
        };

        this._options.liveRegionLabels = liveRegionLabels;
    }

    /**
     * Update the live region for screen readers
     */
    _handleLiveRegionChange() {
        const counter = this._state.results;
        const singleSentence = counter === 0 ? this._options.liveRegionLabels.empty : this._options.liveRegionLabels.single;
        const multipleSentence = counter < this._state.maxResults ? this._options.liveRegionLabels.multiple : this._options.liveRegionLabels.all;
        const prefix = counter > 1 ? multipleSentence : singleSentence;
        const tag = this._state.activeCategories.length > 0 ? [...new Set(this._state.activeCategories)].join(', ') : '';
        const counterText = counter > 0 ? counter : '';
        this._elements.liveRegionCounter.textContent = `${counterText} ${prefix} ${tag}`;
    }

}

export default SearchCategories;
/* eslint-enable */
